import React, { useState, useMemo, useRef, useEffect } from "react";
import { ChevronDown, Search, X } from "lucide-react";

/**
 * MultiSelectFilter — a dropdown trigger + popover panel containing:
 *   • a text search input that filters visible options as the user types
 *   • a Select All checkbox that (de)selects only currently visible options
 *   • per-option checkboxes for multi-select
 *   • a badge on the trigger with the current selection count when >1
 *   • a Clear link inside the panel
 *
 * Feb 27 2026 — used by the Orders toolbar (Marketplaces, etc.).
 *
 * Props:
 *   label           — plain-language label shown on the trigger when empty
 *   options         — array<string>
 *   value           — array<string> currently selected (empty = "all")
 *   onChange        — (nextValue: array<string>) => void
 *   testId          — root data-testid; child testIds derive from it
 *   className       — optional extra classes for the trigger
 */
export default function MultiSelectFilter({ label, options, value, onChange, testId, className = "", triggerClassName }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const rootRef = useRef(null);
  const selected = new Set(value || []);
  const filtered = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => String(o).toLowerCase().includes(q));
  }, [options, search]);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  const toggle = (opt) => {
    const next = new Set(selected);
    if (next.has(opt)) next.delete(opt); else next.add(opt);
    onChange([...next]);
  };
  const allVisibleSelected = filtered.length > 0 && filtered.every((o) => selected.has(o));
  const selectAllVisible = (e) => {
    const next = new Set(selected);
    if (e.target.checked) filtered.forEach((o) => next.add(o));
    else filtered.forEach((o) => next.delete(o));
    onChange([...next]);
  };
  const clearAll = () => onChange([]);
  const triggerLabel = () => {
    if (selected.size === 0) return `All ${label}`;
    return `${label} (${selected.size})`;
  };
  return (
    <div className={`relative ${className}`} ref={rootRef} data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        data-testid={`${testId}-trigger`}
        className={triggerClassName || "h-8 px-2 text-[12.5px] border border-slate-200 rounded-sm bg-white inline-flex items-center gap-1.5 hover:bg-slate-50 min-w-[150px] justify-between"}
      >
        <span className="truncate">{triggerLabel()}</span>
        {selected.size >= 1 && (
          <span
            data-testid={`${testId}-count-badge`}
            className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 text-[9.5px] font-bold text-white bg-blue-600 rounded-full"
          >
            {selected.size}
          </span>
        )}
        <ChevronDown size={12} className="text-slate-500" />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 z-40 bg-white border border-slate-200 rounded-sm shadow-lg min-w-[240px] max-h-[360px] flex flex-col">
          {/* Search */}
          <div className="relative px-2 pt-2">
            <Search size={11} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              autoFocus
              data-testid={`${testId}-search`}
              className="h-7 w-full pl-7 pr-2 text-[12px] border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          {/* Select All */}
          <label className="flex items-center gap-2 px-3 py-1.5 text-[12px] border-b border-slate-100 mt-2 cursor-pointer hover:bg-slate-50">
            <input
              type="checkbox"
              checked={allVisibleSelected}
              onChange={selectAllVisible}
              data-testid={`${testId}-select-all`}
              className="cursor-pointer"
            />
            <span className="font-semibold text-slate-700">Select all</span>
            <span className="ml-auto font-mono text-[10.5px] text-slate-500">{selected.size}/{options.length}</span>
          </label>
          {/* Options */}
          <div className="flex-1 overflow-y-auto">
            {filtered.length === 0 && (
              <div className="px-3 py-4 text-[12px] text-slate-500 italic">No matches</div>
            )}
            {filtered.map((opt) => (
              <label
                key={opt}
                className="flex items-center gap-2 px-3 py-1.5 text-[12.5px] hover:bg-slate-50 cursor-pointer border-b border-slate-50 last:border-0"
                data-testid={`${testId}-option-${opt}`}
              >
                <input
                  type="checkbox"
                  checked={selected.has(opt)}
                  onChange={() => toggle(opt)}
                  className="cursor-pointer"
                />
                <span className="truncate">{opt}</span>
              </label>
            ))}
          </div>
          {/* Clear */}
          <div className="px-3 py-1.5 border-t border-slate-100 flex items-center justify-between">
            <button
              type="button"
              onClick={clearAll}
              data-testid={`${testId}-clear`}
              className="text-[11px] text-red-600 hover:text-red-700 inline-flex items-center gap-1"
            >
              <X size={10} /> Clear
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-[11px] font-semibold text-slate-600 hover:text-slate-900"
            >Done</button>
          </div>
        </div>
      )}
    </div>
  );
}
